/**
 * Property Video Slideshow Generator - Reliable Build v2
 * 
 * Uses synchronous frame capture with explicit frame counting
 * to ensure video terminates at the correct time.
 */

import { RoomType } from '@/types/property';

export interface PhotoWithRoomType {
  url: string;
  roomType?: RoomType;
}

export interface SlideshowOptions {
  photos: string[] | PhotoWithRoomType[];
  photoDuration?: number;
  transitionDuration?: number;
  width?: number;
  height?: number;
  fps?: number;
}

export interface SlideshowResult {
  success: boolean;
  videoUrl?: string;
  blob?: Blob;
  duration?: number;
  error?: string;
}

// Room ordering for professional flow
const ROOM_PRIORITY: Record<RoomType, number> = {
  'exterior': 0,
  'living': 1,
  'dining': 2,
  'kitchen': 3,
  'office': 4,
  'bedroom': 5,
  'bathroom': 6,
  'laundry': 7,
  'garage': 8,
  'other': 9,
};

// Ken Burns zoom presets
const ZOOM_PRESETS = [
  { startScale: 1.0, endScale: 1.08, startX: 0, endX: -0.02, startY: 0, endY: -0.02 },
  { startScale: 1.08, endScale: 1.0, startX: -0.02, endX: 0.02, startY: -0.01, endY: 0.01 },
  { startScale: 1.0, endScale: 1.06, startX: 0, endX: 0.02, startY: 0, endY: -0.01 },
  { startScale: 1.06, endScale: 1.0, startX: 0.01, endX: -0.01, startY: -0.01, endY: 0.01 },
];

function easeInOutQuad(t: number): number {
  return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
}

async function loadImage(src: string): Promise<HTMLImageElement> {
  const fromSrc = (s: string) => new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    const timeout = setTimeout(() => reject(new Error('Timeout')), 20000);
    img.onload = () => { clearTimeout(timeout); resolve(img); };
    img.onerror = () => { clearTimeout(timeout); reject(new Error('Load failed')); };
    img.src = s;
  });

  try {
    return await fromSrc(src);
  } catch (err) {
    // Remote host may not send CORS headers on <img> requests — retry by
    // fetching the bytes and loading them from an object URL instead.
    if (/^https?:/i.test(src)) {
      const res = await fetch(src, { mode: 'cors' });
      if (!res.ok) throw err;
      const objectUrl = URL.createObjectURL(await res.blob());
      try {
        return await fromSrc(objectUrl);
      } finally {
        setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
      }
    }
    throw err;
  }
}

/**
 * Frame clock that keeps ticking when the tab is hidden.
 *
 * `requestAnimationFrame` stops entirely in background tabs and window
 * `setTimeout` is clamped to ~1s, which stalls (or truncates) video capture
 * when the user switches away during a long listing generation. A Web Worker
 * timer is not throttled, so we drive frames from there and fall back to
 * rAF/timeouts if workers are unavailable.
 */
function createFrameClock(intervalMs: number, onTick: () => void): () => void {
  try {
    const src = `let id=null;onmessage=(e)=>{if(e.data&&e.data.type==='start'){id=setInterval(()=>postMessage('t'),e.data.interval);}else{clearInterval(id);close();}};`;
    const blobUrl = URL.createObjectURL(new Blob([src], { type: 'application/javascript' }));
    const worker = new Worker(blobUrl);
    worker.onmessage = () => onTick();
    worker.postMessage({ type: 'start', interval: Math.max(4, Math.floor(intervalMs)) });
    return () => {
      try { worker.postMessage({ type: 'stop' }); } catch { /* noop */ }
      worker.terminate();
      URL.revokeObjectURL(blobUrl);
    };
  } catch {
    const id = setInterval(onTick, Math.max(4, Math.floor(intervalMs)));
    return () => clearInterval(id);
  }
}


function sortPhotos(photos: PhotoWithRoomType[]): PhotoWithRoomType[] {
  const exteriors = photos.filter(p => p.roomType === 'exterior');
  const interiors = photos.filter(p => p.roomType !== 'exterior');
  
  interiors.sort((a, b) => {
    const pa = ROOM_PRIORITY[a.roomType || 'other'];
    const pb = ROOM_PRIORITY[b.roomType || 'other'];
    return pa - pb;
  });
  
  const sorted: PhotoWithRoomType[] = [];
  if (exteriors.length > 0) sorted.push(exteriors[0]);
  sorted.push(...interiors);
  if (exteriors.length > 1) sorted.push(...exteriors.slice(1));
  
  return sorted;
}

function drawCover(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  w: number, h: number,
  scale: number, offsetX: number, offsetY: number,
  alpha: number
): void {
  const imgRatio = img.width / img.height;
  const canvasRatio = w / h;
  
  let dw: number, dh: number;
  if (imgRatio > canvasRatio) {
    dh = h * scale;
    dw = dh * imgRatio;
  } else {
    dw = w * scale;
    dh = dw / imgRatio;
  }
  
  const x = (w - dw) / 2 + offsetX * w;
  const y = (h - dh) / 2 + offsetY * h;
  
  ctx.globalAlpha = alpha;
  ctx.drawImage(img, x, y, dw, dh);
  ctx.globalAlpha = 1;
}

function drawVignette(ctx: CanvasRenderingContext2D, w: number, h: number): void {
  const gradient = ctx.createRadialGradient(w/2, h/2, h*0.35, w/2, h/2, h*0.8);
  gradient.addColorStop(0, 'rgba(0,0,0,0)');
  gradient.addColorStop(1, 'rgba(0,0,0,0.18)');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, w, h);
}

export async function generateSlideshowVideo(options: SlideshowOptions): Promise<SlideshowResult> {
  const {
    photos: inputPhotos,
    photoDuration = 5,
    transitionDuration = 1,
    width = 1280,
    height = 720,
    fps = 30,
  } = options;

  if (!inputPhotos || inputPhotos.length === 0) {
    return { success: false, error: 'No photos provided' };
  }

  console.log('🎬 Starting video generation...');
  const startTime = performance.now();

  try {
    // Normalize photos
    const normalized: PhotoWithRoomType[] = inputPhotos.map(p =>
      typeof p === 'string' ? { url: p, roomType: 'other' as RoomType } : p
    );
    const sorted = sortPhotos(normalized);
    
    // Load images
    console.log(`📷 Loading ${sorted.length} images...`);
    const loadResults = await Promise.allSettled(sorted.map(p => loadImage(p.url)));
    const images = loadResults
      .filter((r): r is PromiseFulfilledResult<HTMLImageElement> => r.status === 'fulfilled')
      .map(r => r.value);
    
    if (images.length === 0) {
      return { success: false, error: 'No images loaded' };
    }
    console.log(`✅ Loaded ${images.length} images`);

    // Calculate frame counts
    const numPhotos = images.length;
    const framesPerPhoto = Math.round(photoDuration * fps);
    const transitionFrames = Math.round(transitionDuration * fps);
    const totalFrames = numPhotos * framesPerPhoto;
    const totalDuration = numPhotos * photoDuration;
    
    console.log(`⏱️ Duration: ${totalDuration}s, Frames: ${totalFrames}`);

    // Setup canvas
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d', { alpha: false })!;

    // Find supported format
    const mimeTypes = ['video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm'];
    const mimeType = mimeTypes.find(m => MediaRecorder.isTypeSupported(m)) || 'video/webm';
    console.log(`📹 Format: ${mimeType}`);

    return new Promise<SlideshowResult>((resolve) => {
      const chunks: Blob[] = [];
      let stopClock: () => void = () => { /* replaced once the clock starts */ };
      // Use captureStream(0) for manual frame control - no automatic capture
      const stream = canvas.captureStream(0);
      const videoTrack = stream.getVideoTracks()[0] as any;

      
      const recorder = new MediaRecorder(stream, {
        mimeType,
        videoBitsPerSecond: 4_000_000,
      });

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };

      recorder.onstop = () => {
        stopClock();
        const blob = new Blob(chunks, { type: mimeType });
        const elapsed = ((performance.now() - startTime) / 1000).toFixed(1);

        // A near-empty blob means capture never really ran (throttled tab,
        // codec failure). Surface it as an error instead of saving a dud file.
        if (blob.size < 10_000) {
          console.error(`❌ Video capture produced an unusable file (${blob.size} bytes)`);
          resolve({ success: false, error: 'Video capture produced an empty file' });
          return;
        }

        const videoUrl = URL.createObjectURL(blob);
        console.log(`✅ Video complete: ${(blob.size / 1024 / 1024).toFixed(2)}MB in ${elapsed}s`);

        resolve({
          success: true,
          videoUrl,
          blob,
          duration: totalDuration,
        });
      };


      recorder.onerror = () => {
        stopClock();
        resolve({ success: false, error: 'Recording failed' });
      };


      // Render a single frame and request capture
      const renderFrame = (frameNum: number) => {
        const photoIdx = Math.min(Math.floor(frameNum / framesPerPhoto), numPhotos - 1);
        const frameInPhoto = frameNum - photoIdx * framesPerPhoto;
        const progress = frameInPhoto / framesPerPhoto;
        
        // Clear canvas
        ctx.fillStyle = '#000';
        ctx.fillRect(0, 0, width, height);
        
        // Calculate transition state
        const framesUntilEnd = framesPerPhoto - frameInPhoto;
        const inTransition = photoIdx < numPhotos - 1 && framesUntilEnd <= transitionFrames;
        
        const effect = ZOOM_PRESETS[photoIdx % ZOOM_PRESETS.length];
        const easedProgress = easeInOutQuad(progress);
        const scale = effect.startScale + (effect.endScale - effect.startScale) * easedProgress;
        const offsetX = effect.startX + (effect.endX - effect.startX) * easedProgress;
        const offsetY = effect.startY + (effect.endY - effect.startY) * easedProgress;
        
        if (inTransition && photoIdx < numPhotos - 1) {
          const transProgress = (transitionFrames - framesUntilEnd) / transitionFrames;
          const easedTrans = easeInOutQuad(transProgress);
          
          // Current photo fading out
          drawCover(ctx, images[photoIdx], width, height, scale, offsetX, offsetY, 1 - easedTrans);
          
          // Next photo fading in
          const nextEffect = ZOOM_PRESETS[(photoIdx + 1) % ZOOM_PRESETS.length];
          const nextScale = nextEffect.startScale;
          const nextOffX = nextEffect.startX;
          const nextOffY = nextEffect.startY;
          drawCover(ctx, images[photoIdx + 1], width, height, nextScale, nextOffX, nextOffY, easedTrans);
        } else {
          // Normal display
          drawCover(ctx, images[photoIdx], width, height, scale, offsetX, offsetY, 1);
        }
        
        drawVignette(ctx, width, height);

        // Manually request frame capture
        if (videoTrack && typeof videoTrack.requestFrame === 'function') {
          videoTrack.requestFrame();
        }
      };

      // Start recording
      recorder.start(100); // collect data every 100ms for reliability
      console.log('🎥 Recording started...');

      let frameIndex = 0;
      const frameInterval = 1000 / fps;
      let lastFrameTime = performance.now();
      let finishing = false;

      const finish = () => {
        if (finishing) return;
        finishing = true;
        renderFrame(totalFrames - 1);
        console.log('🏁 Finalizing...');
        setTimeout(() => {
          if (recorder.state === 'recording') recorder.stop();
        }, 500);
      };

      const tick = () => {
        if (finishing) return;
        const now = performance.now();
        const elapsed = now - lastFrameTime;

        if (elapsed >= frameInterval) {
          // Catch up if the clock was coarse (hidden tab, busy main thread).
          const framesToRender = Math.max(1, Math.floor(elapsed / frameInterval));
          for (let i = 0; i < framesToRender && frameIndex < totalFrames; i++) {
            renderFrame(frameIndex);
            frameIndex++;
          }
          lastFrameTime = now - (elapsed % frameInterval);

          if (frameIndex % fps < framesToRender || frameIndex >= totalFrames) {
            const pct = Math.round((frameIndex / totalFrames) * 100);
            console.log(`🎞️ ${pct}% (${Math.round(frameIndex / fps)}s / ${totalDuration}s) photo ${Math.min(Math.floor(frameIndex / framesPerPhoto) + 1, numPhotos)}/${numPhotos}`);
          }
        }

        if (frameIndex >= totalFrames) finish();
      };

      // Worker-driven clock: keeps rendering even if the tab is backgrounded.
      stopClock = createFrameClock(frameInterval, tick);

      // Safety timeout
      setTimeout(() => {
        if (recorder.state === 'recording') {
          console.warn('⚠️ Timeout - forcing stop');
          recorder.stop();
        }
      }, (totalDuration + 30) * 1000);
    });


  } catch (error) {
    console.error('❌ Error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

export function downloadVideo(blob: Blob, filename = 'property-video.webm'): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function calculateVideoDuration(photoCount: number, photoDuration = 5): number {
  return photoCount * photoDuration;
}
